// Form errors
export type FormErrors = Partial<Record<keyof FormValues, string>>;

// Maps to: "Do you work for a Solution or Marketplace Partner?"
export type PartnerType = 'No' | 'Solution Partner' | 'Marketplace Partner';

// Maps to: "How do you want to contribute as an Atlassian Champion?"
export type ChampionPreference =
  | 'Community Forums Contributor'
  | 'Community Event Organizer'
  | 'Creator'
  | 'Trainer/Mentor'
  | 'CUG Leader'
  | 'CoE Member'
  | null;

/** Success Champion tracks under “How do you want to contribute…” (managed accounts only in UI) */
export const SUCCESS_CHAMPION_PREFERENCES: NonNullable<ChampionPreference>[] = [
  'Trainer/Mentor',
  'CUG Leader',
  'CoE Member',
];

export type YesNo = 'yes' | 'no' | null;

export type YesNoUnsure = 'Yes' | 'No' | 'Unsure' | null;

// Maps to: "What's the focus of your Center of Excellence?"
export type CoeFocus = 'Project Management' | 'Agile Methodologies' | 'Other' | null;

export type TrainingSource =
  | 'Atlassian On-Demand Learning'
  | 'Solution Partner provided training'
  | 'In-house training delivered by your organization'
  | 'I create my own training'
  | 'Other';

// How did you find out about the Atlassian Champions program?
export type HowHeard =
  | 'Atlassian Enterprise Advocate (EA)'
  | 'Atlassian Technical Account Manager (TAM)'
  | 'Atlassian Customer Success Manager'
  | 'Atlassian Employee (Other than EA or TAM)'
  | 'Atlassian Community Leader'
  | 'Atlassian Solution Partner'
  | 'community.atlassian.com'
  | 'Email from Atlassian'
  | 'Atlassian Corporate Event (i.e. Team Tour, Open)'
  | 'Social media'
  | 'Teammate'
  | 'Another user group (Agile, DevOps, project management, etc.)'
  | 'Other'
  | null;

// -----------------------------
// Full form data structure
// -----------------------------

export type FormValues = {
  /** Atlassian Account ID — seeded from session when the form loads; not shown in the UI */
  aaid: string;

  // 1. Personal info + contact (top of Salesforce form)
  firstName: string;
  lastName: string;
  email: string;
  communityProfileUrl: string;
  linkedinUrl: string;

  // 2. Address + birthdate
  street: string;
  city: string;
  stateProvince: string;
  postalCode: string;
  country: string;
  // Text input with placeholder "MM/DD/YYYY", same as Salesforce
  birthDate: string;

  // 3. Work & partner info (checkbox group — multiple selections allowed)
  partnerType: PartnerType[];

  // 4. Atlassian products used (checkbox group)
  products: string[]; // will hold IDs like 'jira', 'confluence', etc.
  // "Please specify" when "Other" is selected (Salesforce: otherAtlassianProducts)
  otherAtlassianProducts: string;

  // 5. Global motivation
  // Maps to: "Why would you like to become an Atlassian Champion?"
  whyBecomeChampion: string;

  // 6. Single top preference (one of the six tracks)
  championPreference: ChampionPreference;

  // --- Contributing to Atlassian Community Forums (conditional) ---
  // Maps to text area: "How do you see yourself contributing to the Community Forums?"
  howContributeOnline: string;

  // --- Organizing Atlassian Community Events (conditional) ---
  // "Have you previously attended an Atlassian Community Event?" (Yes/No)
  attendedAce: YesNo;
  // "Please tell us about your experience." (conditional: visible when attendedAce === 'yes')
  aceExperience: string;
  // "Can you commit to host at least one event every 90 days…?"
  fourEventsPerYear: 'Yes' | 'No' | 'Maybe, I need more information' | null;
  // "What kind of activities and events are you hoping to organize?"
  activityAndEvent: string;

  // --- Creating original thought leadership or learning content (conditional) ---
  // "Why do you create Atlassian-focused content on social media?"
  whyToCreateContent: string;
  // "How often do you currently share your own content…?" dropdown
  contentSharingFrequency:
    | 'less_than_once_a_month'
    | 'once_a_month'
    | 'every_two_weeks'
    | 'weekly'
    | 'multiple_times_a_week'
    | '';
  // "Share three examples of original content…"
  bestContent1: string;
  bestContent2: string;
  bestContent3: string;
  // "Primary social media profile URL"
  primarySocialProfile: string;
  // "Other social media profiles"
  otherURL: string;
  // "Can you commit to sharing 2 pieces of content per month?" (Yes/No)
  commitSharingContent: YesNo;

  // --- Creating or delivering Atlassian training for end users (conditional) ---
  // "How often do you build training on Atlassian for end users?"
  trainingFrequency: 'Weekly' | 'Monthly' | 'Quarterly' | 'Yearly' | '';
  // "What does your organization currently use for Atlassian training? Select all that apply."
  trainingSources: TrainingSource[];

  // --- Running Atlassian-focused CUG / training events (conditional) ---
  // "Do you currently have an active Atlassian focused Company User Group…?"
  activeCug: YesNoUnsure;
  // "How many Company User Group sessions or other training events do you run each year?"
  eventsPerYear: 'Up to 4' | '5+' | '10+' | '';

  // --- Contributing in a Center of Excellence ---
  // "Do you currently discuss Atlassian in a Center of Excellence?"
  discussInCoE: YesNo;
  // "What's the focus of your Center of Excellence?"
  coeFocus: CoeFocus;
  // "Other" (conditional: visible when coeFocus === 'other')
  otherExcellence: string;

  // 7. How did you find out about the Atlassian Champions program?
  howHeard: HowHeard;
  howHeardOther: string;
  // "Please specify" (conditional: visible when howHeard === 'employee')
  howHeardEmployee: string;
  // "Referrer Name" (conditional: visible when howHeard === 'champion')
  referrerName: string;

  // 8. Agreements (end of form)
  /** Atlassian Champion Program Agreement (always shown in signup agreements) */
  consentProgramAgreement: boolean;
  consentPrivacyPolicy: boolean;
  consentNda: boolean;
  /** Shown when Success Champion track selected — data use / profile activity consent */
  consentSuccessChampionAgreement: boolean;
};

// -----------------------------
// Initial form values (blank form)
// -----------------------------

export const initialValues: FormValues = {
  aaid: '',
  firstName: '',
  lastName: '',
  email: '',
  communityProfileUrl: '',
  linkedinUrl: '',

  street: '',
  city: '',
  stateProvince: '',
  postalCode: '',
  country: '',
  birthDate: '',

  partnerType: [],

  products: [],
  otherAtlassianProducts: '',

  whyBecomeChampion: '',

  championPreference: null,

  howContributeOnline: '',

  attendedAce: null,
  aceExperience: '',
  fourEventsPerYear: null,
  activityAndEvent: '',

  whyToCreateContent: '',
  contentSharingFrequency: '',
  bestContent1: '',
  bestContent2: '',
  bestContent3: '',
  primarySocialProfile: '',
  otherURL: '',
  commitSharingContent: null,

  trainingFrequency: '',
  trainingSources: [],

  activeCug: null,
  eventsPerYear: '',

  discussInCoE: null,
  coeFocus: null,
  otherExcellence: '',

  howHeard: null,
  howHeardOther: '',
  howHeardEmployee: '',
  referrerName: '',

  consentProgramAgreement: false,
  consentPrivacyPolicy: false,
  consentNda: false,
  consentSuccessChampionAgreement: false,
};
